import { ref, computed, watch, onMounted, type ComputedRef } from 'vue'
import { isImageAccessible } from '@lib/util/image'

interface UseImageAccessibilityOptions {
  thumbnailUrl: ComputedRef<string | null>
  hasAIEnabled: ComputedRef<boolean>
  hasFile: ComputedRef<boolean>
}

export default function useImageAccessibility({ thumbnailUrl, hasAIEnabled, hasFile }: UseImageAccessibilityOptions) {
  const imageAccessible = ref<boolean | null>(null)
  let accessibilityCheckId = 0

  const hasValidThumbnail = computed(() => {
    const thumbnail = thumbnailUrl.value

    if (thumbnail) {
      return imageAccessible.value !== false
    }

    if (hasAIEnabled.value && hasFile.value) {
      return imageAccessible.value !== false
    }

    return false
  })

  async function checkImageAccessibility() {
    const urlToCheck = thumbnailUrl.value

    const currentCheckId = ++accessibilityCheckId

    if (!urlToCheck) {
      if (currentCheckId === accessibilityCheckId) {
        imageAccessible.value = null
      }
      return
    }

    try {
      await isImageAccessible(urlToCheck)
      if (currentCheckId === accessibilityCheckId) {
        imageAccessible.value = true
      }
    } catch {
      if (currentCheckId === accessibilityCheckId) {
        imageAccessible.value = false
      }
    }
  }

  function handleImageError() {
    imageAccessible.value = false
  }

  watch(
    () => thumbnailUrl.value,
    () => {
      imageAccessible.value = null
      checkImageAccessibility()
    },
  )

  onMounted(() => {
    checkImageAccessibility()
  })

  return {
    imageAccessible,
    hasValidThumbnail,
    handleImageError,
    checkImageAccessibility,
  }
}
